import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Box } from "@mui/material";

import { ActivityTraceIdCopy } from "./ActivityTraceIdCopy";
import { AlertContext } from "./AlertContext";
import { AlertContextValue, AlertDescriptor, AlertLevel } from "./AlertContextProvider.types";
import { ALERT_SCREEN_EDGE_OFFSET_PX, AlertSnackbar } from "./AlertSnackbar";
import { ErrorFieldLink } from "./ErrorFieldLink";
import { setAlertAccessor } from "./alertAccessor";

export const ALERT_DURATION = 5000;

/**
 * Snackbar/toast notifications shared by both apps: error/warning/info/success + close, de-duplicated
 * by message, auto-dismissing when a duration is set, dismissable with Escape. Errors can carry a
 * copyable activity id ({@link showErrorWithActivityId}) or a jump-to-first-error link
 * ({@link showFormValidationError}); a module accessor (setAlertAccessor) lets non-React code (e.g. an
 * HTTP error middleware) raise a global error toast.
 */
export const AlertContextProvider: React.FC<PropsWithChildren> = ({ children }: PropsWithChildren) => {
  const [alerts, setAlerts] = useState<AlertDescriptor[]>([]);
  const alertsRef = useRef(alerts);
  const timeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  const removeAlert = useCallback((id: string): void => {
    const timeoutId = timeouts.current.get(id);
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeouts.current.delete(id);
    }
    setAlerts((current) => current.filter((alert) => alert.id !== id));
  }, []);

  const addAlert = useCallback(
    (
      message: string,
      level: AlertLevel,
      additionalComponent: ReactNode | undefined,
      timeout: number | null
    ): string => {
      const existing = alertsRef.current.find((a) => a.message === message);
      if (existing) {
        return existing.id;
      }

      const id = crypto.randomUUID();
      setAlerts((current) => [...current, { id, level, message, additionalComponent, duration: timeout }]);

      if (timeout) {
        const timeoutId = setTimeout(() => {
          timeouts.current.delete(id);
          setAlerts((current) => current.filter((alert) => alert.id !== id));
        }, timeout);
        timeouts.current.set(id, timeoutId);
      }

      return id;
    },
    []
  );

  const showError = useCallback(
    (message: string, duration: number | undefined | null = null) => addAlert(message, "error", undefined, duration),
    [addAlert]
  );

  const showErrorWithActivityId = useCallback(
    (message: string, activityId: string, duration: number | undefined | null = null) =>
      addAlert(message, "error", <ActivityTraceIdCopy activityId={activityId} />, duration),
    [addAlert]
  );

  const showFormValidationError = useCallback(
    (message: string, onClick: () => void, duration: number | undefined | null = null) =>
      addAlert(message, "error", <ErrorFieldLink onClick={onClick} />, duration),
    [addAlert]
  );

  const showWarning = useCallback(
    (message: string, additionalMessage?: string, duration: number | undefined | null = null) =>
      addAlert(message, "warning", additionalMessage, duration),
    [addAlert]
  );

  const showInfo = useCallback(
    (message: string, additionalMessage?: string, duration: number | undefined | null = ALERT_DURATION) =>
      addAlert(message, "info", additionalMessage, duration),
    [addAlert]
  );

  const showSuccess = useCallback(
    (message: string, additionalMessage?: string, duration: number | undefined | null = ALERT_DURATION) =>
      addAlert(message, "success", additionalMessage, duration),
    [addAlert]
  );

  useEffect(() => {
    setAlertAccessor((message: string, activityTraceId?: string) => {
      if (activityTraceId) {
        showErrorWithActivityId(message, activityTraceId);
      } else {
        showError(message);
      }
    });
    return () => setAlertAccessor(null);
  }, [showError, showErrorWithActivityId]);

  useEffect(() => {
    if (alerts.length === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
        const lastAlert = alerts[alerts.length - 1];
        if (lastAlert) {
          removeAlert(lastAlert.id);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [alerts, removeAlert]);

  const closeAlert = useCallback((alertId: string) => removeAlert(alertId), [removeAlert]);

  const contextValue: AlertContextValue = {
    showError,
    showErrorWithActivityId,
    showFormValidationError,
    showWarning,
    showInfo,
    showSuccess,
    closeAlert,
  };

  return (
    <>
      <AlertContext.Provider value={contextValue}>{children}</AlertContext.Provider>
      <Box
        sx={{
          position: "fixed",
          bottom: 84,
          right: ALERT_SCREEN_EDGE_OFFSET_PX,
          display: "flex",
          flexDirection: "column-reverse",
          gap: 3,
          zIndex: (theme) => theme.zIndex.snackbar,
        }}
      >
        {alerts.map((alert) => (
          <AlertSnackbar key={alert.id} alert={alert} onClose={removeAlert} />
        ))}
      </Box>
    </>
  );
};
